import { Menu, Transition } from '@headlessui/react'
import { Fragment, useRef, useEffect, useState } from 'react'
import { MoreVertical, Eye, Edit, Trash2, Copy, Download, Activity, Scale } from 'lucide-react'
import { clsx } from 'clsx'

export default function ActionsMenu({ 
  items = [],
  align = 'right',
  size = 'md'
}) {
  const buttonRef = useRef(null)
  const [position, setPosition] = useState({ top: 0, left: 0, right: 'auto' })
  const [isOpen, setIsOpen] = useState(false)

  const alignClasses = {
    left: 'origin-top-left',
    right: 'origin-top-right',
  }

  const sizeClasses = {
    sm: 'w-40',
    md: 'w-48',
    lg: 'w-56',
  }

  const iconMap = {
    view: Eye,
    edit: Edit,
    delete: Trash2,
    copy: Copy,
    download: Download,
    activity: Activity,
    scale: Scale,
  }

  // Calcular posición del menú cuando se abre
  const calculatePosition = () => {
    if (!buttonRef.current) return
    
    const button = buttonRef.current
    const rect = button.getBoundingClientRect()
    const viewportWidth = window.innerWidth
    const viewportHeight = window.innerHeight
    const menuWidth = size === 'sm' ? 160 : size === 'md' ? 192 : 224
    const menuHeight = items.length * 42 + 16 // Aproximado
    
    let left = rect.left
    let top = rect.bottom + 8
    let right = 'auto'

    // Ajustar horizontalmente según align y espacio disponible
    if (align === 'right') {
      left = rect.right - menuWidth
      // Si no cabe a la derecha, alinear a la izquierda
      if (left < 8) {
        left = rect.left
        right = 'auto'
      }
      // Si no cabe a la izquierda, usar right
      if (left + menuWidth > viewportWidth - 8) {
        right = viewportWidth - rect.right
        left = 'auto'
      }
    } else {
      // Si no cabe a la izquierda, usar right
      if (left + menuWidth > viewportWidth - 8) {
        right = viewportWidth - rect.left
        left = 'auto'
      }
    }

    // Ajustar verticalmente si no cabe abajo
    if (top + menuHeight > viewportHeight - 8) {
      top = rect.top - menuHeight - 8
      // Si tampoco cabe arriba, ajustar al viewport
      if (top < 8) {
        top = 8
      }
    }

    setPosition({ top, left, right })
  }

  useEffect(() => {
    if (isOpen) {
      calculatePosition()
      // Recalcular en scroll y resize
      const handleUpdate = () => calculatePosition()
      window.addEventListener('scroll', handleUpdate, true)
      window.addEventListener('resize', handleUpdate)
      return () => {
        window.removeEventListener('scroll', handleUpdate, true)
        window.removeEventListener('resize', handleUpdate)
      }
    }
  }, [isOpen, align, size, items.length])

  return (
    <Menu as="div" className="relative inline-block text-left">
      {({ open }) => {
        // Sincronizar estado de apertura para calcular posición
        if (open !== isOpen) {
          setIsOpen(open)
        }
        
        return (
          <>
            <Menu.Button 
              ref={buttonRef}
              className="p-1.5 rounded-lg text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors"
            >
              <MoreVertical className="h-5 w-5" />
            </Menu.Button>

            <Transition
              as={Fragment}
              show={open}
              enter="transition ease-out duration-100"
              enterFrom="transform opacity-0 scale-95"
              enterTo="transform opacity-100 scale-100"
              leave="transition ease-in duration-75"
              leaveFrom="transform opacity-100 scale-100"
              leaveTo="transform opacity-0 scale-95"
            >
              <Menu.Items 
                static
                className={clsx(
                  'fixed z-[9999] mt-0 rounded-lg bg-white dark:bg-gray-800 shadow-lg ring-1 ring-black/5 dark:ring-gray-700 focus:outline-none divide-y divide-gray-100 dark:divide-gray-700',
                  alignClasses[align],
                  sizeClasses[size]
                )}
                style={{
                  top: typeof position.top === 'number' ? `${position.top}px` : 'auto',
                  left: typeof position.left === 'number' ? `${position.left}px` : 'auto',
                  right: typeof position.right === 'number' ? `${position.right}px` : position.right,
                }}
              >
          <div className="py-1">
            {items.map((item, index) => {
              if (item.divider) {
                return <div key={`divider-${index}`} className="border-t border-gray-200 dark:border-gray-700 my-1" />
              }
              
              const Icon = item.icon ? iconMap[item.icon] || item.icon : null
              
              return (
                <Menu.Item key={index}>
                  {({ active, close }) => (
                    <button
                      onClick={(e) => {
                        e.preventDefault()
                        e.stopPropagation()
                        console.log('🔍 Click en item del menú:', item.label)
                        if (item.onClick) {
                          console.log('🔍 Ejecutando onClick del item')
                          try {
                            item.onClick()
                            close() // Cerrar menú después de hacer click
                          } catch (error) {
                            console.error('❌ Error al ejecutar onClick:', error)
                          }
                        }
                      }}
                      disabled={item.disabled}
                      className={clsx(
                        'group flex w-full items-center px-4 py-2.5 text-sm transition-colors',
                        item.danger
                          ? 'text-red-700 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20'
                          : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700',
                        active && (item.danger ? 'bg-red-50 dark:bg-red-900/20' : 'bg-gray-100 dark:hover:bg-gray-700'),
                        item.disabled && 'opacity-50 cursor-not-allowed pointer-events-none'
                      )}
                    >
                      {Icon && (
                        <Icon className={clsx(
                          'mr-3 h-5 w-5 flex-shrink-0',
                          item.danger ? 'text-red-400' : 'text-gray-400 dark:text-gray-500'
                        )} />
                      )}
                      {item.label}
                    </button>
                  )}
                </Menu.Item>
              )
            })}
              </div>
            </Menu.Items>
          </Transition>
          </>
        )
      }}
    </Menu>
  )
}
